import React, { useState, useEffect } from 'react';
import { Settings, Bell, CheckCircle, Trash2, Trophy, Lock, Eye, EyeOff, Check, CreditCard, ArrowLeft, Home, User, LogOut } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { auth, db } from '../firebase';
import { doc, updateDoc, collection, getDocs, writeBatch } from 'firebase/firestore';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider, deleteUser } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import StreakManager from '../components/dashboard/StreakManager';
import notificationService from '../services/notificationService';
import GeometricBackground from '../components/ui/GeometricBackground';
import GrammarBottomNav from '../components/grammar/GrammarBottomNav';

// Design tokens, human-shaped, neuro-aesthetic
const DEEP_TEAL = '#006D77';
const SAGE = '#83C5BE';
const CORAL = '#E29578';
const TEXT_HEADING = '#1E1E1E';
const TEXT_DESC = '#636E72';

// Mobile-optimized ToggleSwitch, 48px touch target, teal/sage, touch feedback
const ToggleSwitch = ({ enabled, setEnabled, label, description }) => (
  <div
    onClick={() => setEnabled(!enabled)}
    className="flex items-center justify-between p-4 rounded-2xl bg-white border border-[#E1E4E8] cursor-pointer active:scale-[0.98] active:bg-[#F8FAFA] transition-all touch-manipulation min-h-[48px]"
  >
    <div className="flex-1 min-w-0 mr-3">
      <p className="font-semibold text-base" style={{ color: TEXT_HEADING }}>{label}</p>
      <p className="text-sm leading-relaxed mt-0.5" style={{ color: TEXT_DESC }}>{description}</p>
    </div>
    <div
      className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors flex-shrink-0 ${enabled ? '' : 'bg-gray-300'}`}
      style={enabled ? { backgroundColor: DEEP_TEAL } : undefined}
    >
      <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
    </div>
  </div>
);

const SettingsPage = ({ userProgress, onLogout }) => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const currentUser = auth.currentUser;
  const location = useLocation();

  const [analyticsConsent, setAnalyticsConsent] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem('logami_analytics_consent') !== 'false';
  });
  const [activeTab, setActiveTab] = useState('account');
  const [mobileView, setMobileView] = useState('menu'); // 'menu' | 'account' | 'notifications' | 'streak'

  const [notificationPrefs, setNotificationPrefs] = useState({
    enabled: false,
    permission: 'default',
    studyReminders: true,
    streakReminders: true,
    reviewReminders: true,
    achievementAlerts: true,
    dailyReminderTime: '18:00',
    quietHoursStart: '22:00',
    quietHoursEnd: '08:00'
  });

  useEffect(() => {
    const loadNotificationPrefs = async () => {
      if (currentUser) {
        const prefs = await notificationService.loadPreferences(currentUser.uid);
        setNotificationPrefs(prefs);
      }
    };
    loadNotificationPrefs();
  }, [currentUser]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    if (tab && ['account', 'notifications', 'streak'].includes(tab)) {
      setActiveTab(tab);
      setMobileView(tab);
    } else {
      setActiveTab('account');
    }
  }, [location.search]);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [dailyGoal, setDailyGoal] = useState(5);
  useEffect(() => {
    if (userProgress?.preferences?.dailyGoal) {
      setDailyGoal(userProgress.preferences.dailyGoal);
    }
  }, [userProgress]);
  const [showPassword, setShowPassword] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmPassword, setDeleteConfirmPassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDailyGoalChange = async (newGoal) => {
    setDailyGoal(newGoal);
    if (!currentUser) return;
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, { 'preferences.dailyGoal': parseInt(newGoal) });
      setSaveStatus('Daily goal updated!');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error updating daily goal:', error);
      setSaveStatus('Error updating goal');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const handleEnableNotifications = async () => {
    try {
      const granted = await notificationService.requestPermission();
      if (granted) {
        const newPrefs = { ...notificationPrefs, enabled: true, permission: 'granted' };
        setNotificationPrefs(newPrefs);
        if (currentUser) await notificationService.savePreferences(currentUser.uid, newPrefs);
        setSaveStatus(t('settings.status.notificationsEnabled'));
        await notificationService.showNotification(t('settings.notifications.welcomeTitle'), {
          body: t('settings.notifications.welcomeBody'),
          tag: 'welcome'
        });
      } else {
        setSaveStatus(t('settings.status.notificationsDenied'));
      }
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error enabling notifications:', error);
      setSaveStatus(t('settings.status.errorEnablingNotifications'));
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const handleNotificationPrefChange = async (key, value) => {
    const newPrefs = { ...notificationPrefs, [key]: value };
    setNotificationPrefs(newPrefs);
    if (currentUser) await notificationService.savePreferences(currentUser.uid, newPrefs);
  };

  const handleAnalyticsConsentChange = async (enabled) => {
    setAnalyticsConsent(enabled);
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('logami_analytics_consent', enabled ? 'true' : 'false');
      }
      if (currentUser) {
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, { 'preferences.analyticsConsent': enabled });
      }
    } catch (error) {
      console.warn('Failed to save analytics consent:', error);
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      setSaveStatus(t('settings.status.passwordsNoMatch'));
      setTimeout(() => setSaveStatus(''), 3000);
      return;
    }
    if (newPassword.length < 6) {
      setSaveStatus(t('settings.status.passwordTooShort'));
      setTimeout(() => setSaveStatus(''), 3000);
      return;
    }
    setIsLoading(true);
    setSaveStatus(t('settings.status.changingPassword'));
    try {
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSaveStatus(t('settings.status.passwordUpdated'));
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        setSaveStatus(t('settings.status.incorrectPassword'));
      } else {
        setSaveStatus(t('settings.status.errorChangingPassword'));
      }
      setTimeout(() => setSaveStatus(''), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setSaveStatus(t('settings.status.deleteTypeDelete'));
      setTimeout(() => setSaveStatus(''), 3000);
      return;
    }
    if (!deleteConfirmPassword) {
      setSaveStatus(t('settings.status.deletePasswordRequired'));
      setTimeout(() => setSaveStatus(''), 3000);
      return;
    }
    setIsDeleting(true);
    setSaveStatus(t('settings.status.deletingAccount'));
    try {
      const credential = EmailAuthProvider.credential(currentUser.email, deleteConfirmPassword);
      await reauthenticateWithCredential(currentUser, credential);
      const userId = currentUser.uid;
      const batch = writeBatch(db);
      const userDocRef = doc(db, 'users', userId);
      batch.delete(userDocRef);
      const subcollections = ['examResults', 'studySessions', 'vocabulary', 'achievements', 'notifications'];
      for (const subcollection of subcollections) {
        const subcollectionRef = collection(db, `users/${userId}/${subcollection}`);
        const snapshot = await getDocs(subcollectionRef);
        snapshot.docs.forEach((d) => batch.delete(d.ref));
      }
      await batch.commit();
      await deleteUser(currentUser);
      setSaveStatus(t('settings.status.accountDeleted'));
      setTimeout(() => { window.location.href = '/'; }, 1500);
    } catch (error) {
      console.error('Error deleting account:', error);
      if (error.code === 'auth/wrong-password') {
        setSaveStatus(t('settings.status.deletePasswordIncorrect'));
      } else if (error.code === 'auth/requires-recent-login') {
        setSaveStatus(t('settings.status.deleteRecentLoginRequired'));
      } else {
        setSaveStatus(t('settings.status.deleteError'));
      }
      setTimeout(() => setSaveStatus(''), 4000);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSave = () => {
    if (activeTab === 'account') {
      if (newPassword) handlePasswordChange();
      else {
        setSaveStatus(t('settings.status.noChanges'));
        setTimeout(() => setSaveStatus(''), 3000);
      }
    } else if (activeTab === 'streak') {
      setSaveStatus('Preferences saved!');
      setTimeout(() => setSaveStatus(''), 3000);
    } else {
      setSaveStatus(t('settings.status.notificationsSaved'));
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const tabs = [
    { id: 'account', label: t('settings.tabs.account'), icon: <Settings className="w-5 h-5 flex-shrink-0" /> },
    { id: 'notifications', label: t('settings.tabs.notifications'), icon: <Bell className="w-5 h-5 flex-shrink-0" /> },
    { id: 'streak', label: t('settings.tabs.streak'), icon: <Trophy className="w-5 h-5 flex-shrink-0" /> },
  ];

  const selectTab = (id) => {
    setActiveTab(id);
    setMobileView(id);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'account':
        return (
          <>
            <h2 className="font-space text-2xl md:text-3xl font-black tracking-tight mb-6 md:mb-8" style={{ color: TEXT_HEADING }}>
              {t('settings.account.title')}
            </h2>
            <div className="space-y-6 md:space-y-8">

              <div className="bg-[#F8FAFA] rounded-2xl p-5 border border-[#E1E4E8]">
                <h3 className="font-space font-bold text-lg mb-3" style={{ color: TEXT_HEADING }}>
                  {t('settings.account.accountInfo')}
                </h3>
                <div className="space-y-2 text-base" style={{ color: TEXT_DESC }}>
                  <p><strong style={{ color: TEXT_HEADING }}>{t('settings.account.user')}</strong> {currentUser?.displayName || 'Not set'}</p>
                  <p><strong style={{ color: TEXT_HEADING }}>{t('settings.account.email')}</strong> {currentUser?.email}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <strong style={{ color: TEXT_HEADING }}>{t('settings.account.verified')}</strong>
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold"
                      style={
                        currentUser?.emailVerified
                          ? { backgroundColor: '#ECFDF5', borderColor: '#A7F3D0', color: '#065F46' }
                          : { backgroundColor: '#FFF7ED', borderColor: '#FDBA74', color: '#9A3412' }
                      }
                    >
                      {currentUser?.emailVerified ? (
                        <CheckCircle className="w-3.5 h-3.5" />
                      ) : (
                        <Lock className="w-3.5 h-3.5" />
                      )}
                      <span>
                        {currentUser?.emailVerified
                          ? t('settings.account.verificationChip.verified')
                          : t('settings.account.verificationChip.unverified')}
                      </span>
                    </span>
                  </div>
                  <p><strong style={{ color: TEXT_HEADING }}>{t('settings.account.memberSince')}</strong> {currentUser?.metadata?.creationTime ? new Date(currentUser.metadata.creationTime).toLocaleDateString('en-US') : t('settings.account.unknown')}</p>
                </div>
              </div>

              <div>
                <h3 className="font-space text-xl font-bold mb-4" style={{ color: TEXT_HEADING }}>
                  {t('settings.account.changePassword')}
                </h3>
                <div className="space-y-4">
                  {[
                    { value: currentPassword, set: setCurrentPassword, key: 'currentPassword', labelKey: 'settings.account.currentPassword' },
                    { value: newPassword, set: setNewPassword, key: 'newPassword', labelKey: 'settings.account.newPassword' },
                    { value: confirmPassword, set: setConfirmPassword, key: 'confirmPassword', labelKey: 'settings.account.confirmPassword' },
                  ].map(({ value, set, key, labelKey }) => (
                    <div key={key}>
                      <label className="block text-sm font-semibold mb-2" style={{ color: TEXT_HEADING }}>
                        {t(labelKey)}
                      </label>
                      <div className="relative min-h-[48px]">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5" style={{ color: TEXT_DESC }} />
                        </div>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={value}
                          onChange={(e) => set(e.target.value)}
                          className="block w-full pl-12 pr-12 py-3 text-base border border-[#E1E4E8] rounded-xl focus:ring-2 focus:ring-[#006D77]/30 focus:border-[#006D77] transition-all touch-manipulation min-h-[48px]"
                          placeholder={t(labelKey)}
                        />
                        {key === 'currentPassword' && (
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-4 flex items-center min-w-[48px] min-h-[48px] justify-center touch-manipulation active:opacity-70"
                            style={{ color: TEXT_DESC }}
                          >
                            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Subscription Management, mobile-first tappable card (aligns with SubscriptionManagementPage UI) */}
              <div className="pt-6 md:pt-8 border-t border-[#E1E4E8]">
                <button
                  onClick={() => navigate('/subscription')}
                  className="w-full text-left rounded-2xl p-4 sm:p-5 border border-[#E1E4E8] bg-white active:scale-[0.99] active:bg-[#F8FAFA] transition-all touch-manipulation min-h-[48px] flex flex-col sm:flex-row sm:items-center gap-4"
                >
                  <div className="flex items-start sm:items-center gap-4 flex-1 min-w-0">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${DEEP_TEAL}15` }}>
                      <CreditCard className="w-6 h-6" style={{ color: DEEP_TEAL }} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-space text-lg font-bold" style={{ color: TEXT_HEADING }}>
                        Plan & Billing
                      </h3>
                      <p className="text-sm mt-0.5" style={{ color: TEXT_DESC }}>
                        View your plan, payment history, and manage access.
                      </p>
                    </div>
                  </div>
                  <span className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-space font-semibold text-base shrink-0 self-start sm:self-center" style={{ backgroundColor: DEEP_TEAL, color: '#fff' }}>
                    Manage
                  </span>
                </button>
              </div>

              <div className="pt-6 md:pt-8 border-t border-[#E1E4E8]">
                <h3 className="font-space text-xl font-bold mb-4" style={{ color: TEXT_HEADING }}>
                  {t('settings.account.dataPrivacy')}
                </h3>
                <div className="space-y-4">
                  <ToggleSwitch
                    enabled={analyticsConsent}
                    setEnabled={handleAnalyticsConsentChange}
                    label={t('settings.account.analyticsConsentLabel', { defaultValue: 'Allow analytics cookies' })}
                    description={t('settings.account.analyticsConsentDescription', { defaultValue: 'Helps us measure feature usage and clicks (Google Analytics via Firebase). You can change this anytime.' })}
                  />
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="w-full sm:w-auto px-6 py-3 min-h-[48px] font-space font-semibold rounded-xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform touch-manipulation border"
                    style={{ backgroundColor: `${CORAL}1A`, color: CORAL, borderColor: `${CORAL}40` }}
                  >
                    <Trash2 className="w-5 h-5" />
                    {t('settings.account.deleteAccount')}
                  </button>
                </div>
              </div>
            </div>
          </>
        );

      case 'streak':
        return (
          <>
            <h2 className="font-space text-xl sm:text-2xl md:text-3xl font-black tracking-tight mb-4 sm:mb-6 md:mb-8" style={{ color: TEXT_HEADING }}>
              {t('settings.streak.title')}
            </h2>
            <div className="space-y-4 sm:space-y-6">
              {/* Daily goal, compact card, thumb-friendly selector */}
              <div className="bg-white rounded-2xl p-4 sm:p-5 border border-[#E1E4E8]">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${DEEP_TEAL}15` }}>
                    <Trophy className="w-5 h-5" style={{ color: DEEP_TEAL }} />
                  </div>
                  <h3 className="font-space font-bold text-base sm:text-lg" style={{ color: TEXT_HEADING }}>{t('settings.streak.dailyGoal')}</h3>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium" style={{ color: TEXT_DESC }}>{t('settings.streak.lessonsPerDay')}</label>
                  <select
                    value={dailyGoal}
                    onChange={(e) => handleDailyGoalChange(e.target.value)}
                    className="block w-full px-4 py-3 min-h-[48px] text-base border border-[#E1E4E8] rounded-xl focus:ring-2 focus:ring-[#006D77]/30 focus:border-[#006D77] bg-[#F8FAFA] transition-all touch-manipulation"
                  >
                    <option value="1">{t('settings.streak.lessonOptions.1')}</option>
                    <option value="3">{t('settings.streak.lessonOptions.3')}</option>
                    <option value="5">{t('settings.streak.lessonOptions.5')}</option>
                    <option value="10">{t('settings.streak.lessonOptions.10')}</option>
                    <option value="15">{t('settings.streak.lessonOptions.15')}</option>
                    <option value="20">{t('settings.streak.lessonOptions.20')}</option>
                  </select>
                </div>
                <p className="text-sm mt-2" style={{ color: TEXT_DESC }}>
                  {t('settings.streak.current', { count: dailyGoal })}
                </p>
              </div>

              <StreakManager streakData={userProgress?.streakData} />

              <div className="space-y-4">
                <ToggleSwitch
                  enabled={notificationPrefs.weekendExcluded || false}
                  setEnabled={(val) => handleNotificationPrefChange('weekendExcluded', val)}
                  label={t('settings.streak.excludeWeekends')}
                  description={t('settings.streak.excludeWeekendsDescription')}
                />
                <ToggleSwitch
                  enabled={notificationPrefs.streakReminders ?? true}
                  setEnabled={(val) => handleNotificationPrefChange('streakReminders', val)}
                  label={t('settings.streak.reminders')}
                  description={t('settings.streak.remindersDescription')}
                />
              </div>
            </div>
          </>
        );

      case 'notifications':
        return (
          <>
            <h2 className="font-space text-2xl md:text-3xl font-black tracking-tight mb-6 md:mb-8" style={{ color: TEXT_HEADING }}>
              {t('settings.notifications.title')}
            </h2>

            <div className={`mb-6 p-5 rounded-2xl border ${notificationPrefs.permission === 'granted' ? 'bg-white border-[#83C5BE]' : 'bg-white border-[#E29578]'}`}>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div>
                  <h3 className="font-space font-bold mb-1" style={{ color: notificationPrefs.permission === 'granted' ? SAGE : CORAL }}>
                    {notificationPrefs.permission === 'granted' ? t('settings.notifications.enabled') : t('settings.notifications.enableTitle')}
                  </h3>
                  <p className="text-base" style={{ color: TEXT_DESC }}>
                    {notificationPrefs.permission === 'granted' ? t('settings.notifications.enabledDescription') : t('settings.notifications.disabledDescription')}
                  </p>
                </div>
                {notificationPrefs.permission !== 'granted' && (
                  <button
                    onClick={handleEnableNotifications}
                    className="px-5 py-3 min-h-[48px] font-space font-semibold rounded-xl flex items-center justify-center active:scale-[0.98] transition-transform touch-manipulation shrink-0"
                    style={{ backgroundColor: DEEP_TEAL, color: '#fff' }}
                  >
                    {t('settings.notifications.enableNow')}
                  </button>
                )}
              </div>
            </div>

            {notificationPrefs.permission === 'granted' && (
              <div className="space-y-4 md:space-y-6">
                <ToggleSwitch
                  enabled={notificationPrefs.enabled}
                  setEnabled={(val) => handleNotificationPrefChange('enabled', val)}
                  label={t('settings.notifications.master')}
                  description={t('settings.notifications.masterDescription')}
                />
                <div className="pt-4 border-t border-[#E1E4E8] space-y-4">
                  <h3 className="font-space font-bold text-lg" style={{ color: TEXT_HEADING }}>{t('settings.notifications.reminderTypes')}</h3>
                  <ToggleSwitch
                    enabled={notificationPrefs.studyReminders}
                    setEnabled={(val) => handleNotificationPrefChange('studyReminders', val)}
                    label={t('settings.notifications.studyReminders')}
                    description={t('settings.notifications.studyDescription')}
                  />
                  <ToggleSwitch
                    enabled={notificationPrefs.streakReminders}
                    setEnabled={(val) => handleNotificationPrefChange('streakReminders', val)}
                    label={t('settings.notifications.streakReminders')}
                    description={t('settings.notifications.streakDescription')}
                  />
                  <ToggleSwitch
                    enabled={notificationPrefs.reviewReminders}
                    setEnabled={(val) => handleNotificationPrefChange('reviewReminders', val)}
                    label={t('settings.notifications.reviewReminders')}
                    description={t('settings.notifications.reviewDescription')}
                  />
                  <ToggleSwitch
                    enabled={notificationPrefs.achievementAlerts}
                    setEnabled={(val) => handleNotificationPrefChange('achievementAlerts', val)}
                    label={t('settings.notifications.achievementAlerts')}
                    description={t('settings.notifications.achievementDescription')}
                  />
                </div>
                {notificationPrefs.studyReminders && (
                  <div className="pt-4 border-t border-[#E1E4E8]">
                    <label className="block text-sm font-semibold mb-2" style={{ color: TEXT_HEADING }}>
                      {t('settings.notifications.dailyReminderTime')}
                    </label>
                    <input
                      type="time"
                      value={notificationPrefs.dailyReminderTime}
                      onChange={(e) => handleNotificationPrefChange('dailyReminderTime', e.target.value)}
                      className="block w-full px-4 py-3 min-h-[48px] text-base border border-[#E1E4E8] rounded-xl focus:ring-2 focus:ring-[#006D77]/30 focus:border-[#006D77] bg-white"
                    />
                  </div>
                )}
                <div className="pt-4 border-t border-[#E1E4E8]">
                  <h3 className="font-space font-bold text-lg mb-2" style={{ color: TEXT_HEADING }}>{t('settings.notifications.quietHours')}</h3>
                  <p className="text-base mb-4" style={{ color: TEXT_DESC }}>{t('settings.notifications.quietHoursDescription')}</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2" style={{ color: TEXT_HEADING }}>{t('settings.notifications.start')}</label>
                      <input
                        type="time"
                        value={notificationPrefs.quietHoursStart}
                        onChange={(e) => handleNotificationPrefChange('quietHoursStart', e.target.value)}
                        className="block w-full px-4 py-3 min-h-[48px] text-base border border-[#E1E4E8] rounded-xl focus:ring-2 focus:ring-[#006D77]/30 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2" style={{ color: TEXT_HEADING }}>{t('settings.notifications.end')}</label>
                      <input
                        type="time"
                        value={notificationPrefs.quietHoursEnd}
                        onChange={(e) => handleNotificationPrefChange('quietHoursEnd', e.target.value)}
                        className="block w-full px-4 py-3 min-h-[48px] text-base border border-[#E1E4E8] rounded-xl focus:ring-2 focus:ring-[#006D77]/30 bg-white"
                      />
                    </div>
                  </div>
                </div>
                <div className="pt-4 border-t border-[#E1E4E8]">
                  <button
                    onClick={async () => {
                      await notificationService.showNotification(t('settings.notifications.testTitle'), { body: t('settings.notifications.testBody') });
                      setSaveStatus(t('settings.status.testSent'));
                      setTimeout(() => setSaveStatus(''), 3000);
                    }}
                    className="px-6 py-3 min-h-[48px] font-space font-semibold rounded-xl border border-[#E1E4E8] bg-white active:scale-[0.98] transition-transform touch-manipulation"
                    style={{ color: TEXT_HEADING }}
                  >
                    {t('settings.notifications.sendTest')}
                  </button>
                </div>
              </div>
            )}

            {!notificationService.isSupported() && (
              <div className="rounded-2xl p-4 mt-4 border" style={{ backgroundColor: `${CORAL}1A`, borderColor: `${CORAL}40` }}>
                <p className="text-base" style={{ color: CORAL }}>{t('settings.notifications.notSupported')}</p>
              </div>
            )}
          </>
        );

      default:
        return null;
    }
  };

  // Bottom Sheet. Delete Account (mobile-first, keyboard-safe)
  const DeleteAccountBottomSheet = () => (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden"
        onClick={() => !isDeleting && (setShowDeleteModal(false), setDeleteConfirmText(''), setDeleteConfirmPassword(''))}
        aria-hidden
      />
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl animate-slide-up max-h-[85vh] overflow-y-auto lg:hidden"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 24px)' }}
      >
        <div className="sticky top-0 bg-white pt-4 pb-2 px-4 flex justify-center rounded-t-3xl">
          <div className="w-12 h-1 rounded-full bg-gray-300" aria-hidden />
        </div>
        <div className="px-4 pb-8">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${CORAL}1A` }}>
              <Trash2 className="w-7 h-7" style={{ color: CORAL }} />
            </div>
            <h2 className="font-space text-xl font-black tracking-tight" style={{ color: TEXT_HEADING }}>{t('settings.deleteModal.title')}</h2>
            <p className="text-sm mt-2" style={{ color: TEXT_DESC }}>{t('settings.deleteModal.warning')}</p>
          </div>
          <div className="rounded-xl p-4 mb-6 border" style={{ backgroundColor: `${CORAL}0D`, borderColor: `${CORAL}40` }}>
            <h3 className="font-semibold mb-2" style={{ color: CORAL }}>{t('settings.deleteModal.willDelete')}</h3>
            <ul className="text-sm space-y-1 list-disc list-inside" style={{ color: TEXT_DESC }}>
              <li>{t('settings.deleteModal.item1')}</li>
              <li>{t('settings.deleteModal.item2')}</li>
              <li>{t('settings.deleteModal.item3')}</li>
              <li>{t('settings.deleteModal.item4')}</li>
            </ul>
          </div>
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: TEXT_HEADING }}>
                {t('settings.deleteModal.typeDelete')} <span className="font-bold" style={{ color: CORAL }}>DELETE</span>
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={t('settings.deleteModal.typeDeletePlaceholder')}
                className="block w-full px-4 py-3 min-h-[48px] text-base border border-[#E1E4E8] rounded-xl focus:ring-2 focus:ring-[#006D77]/30"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: TEXT_HEADING }}>{t('settings.deleteModal.enterPassword')}</label>
              <input
                type="password"
                value={deleteConfirmPassword}
                onChange={(e) => setDeleteConfirmPassword(e.target.value)}
                placeholder={t('settings.deleteModal.passwordPlaceholder')}
                className="block w-full px-4 py-3 min-h-[48px] text-base border border-[#E1E4E8] rounded-xl focus:ring-2 focus:ring-[#006D77]/30"
              />
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); setDeleteConfirmPassword(''); }}
              disabled={isDeleting}
              className="w-full py-3 min-h-[48px] font-space font-semibold rounded-xl border border-[#E1E4E8] active:scale-[0.98] transition-transform touch-manipulation disabled:opacity-50"
              style={{ color: TEXT_HEADING }}
            >
              {t('settings.deleteModal.cancel')}
            </button>
            <button
              onClick={handleDeleteAccount}
              disabled={isDeleting || deleteConfirmText !== 'DELETE' || !deleteConfirmPassword}
              className="w-full py-3 min-h-[48px] font-space font-semibold rounded-xl active:scale-[0.98] transition-transform touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: CORAL, color: '#fff' }}
            >
              {isDeleting ? t('settings.deleteModal.deleting') : t('settings.deleteModal.deleteForever')}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  // Desktop fallback modal (lg and up), keep a simple centered modal for large screens
  const DeleteAccountModalDesktop = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm hidden lg:flex">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 md:p-8 border border-[#E1E4E8]">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${CORAL}1A` }}>
            <Trash2 className="w-8 h-8" style={{ color: CORAL }} />
          </div>
          <h2 className="font-space text-2xl font-black tracking-tight" style={{ color: TEXT_HEADING }}>{t('settings.deleteModal.title')}</h2>
          <p className="text-sm mt-2" style={{ color: TEXT_DESC }}>{t('settings.deleteModal.warning')}</p>
        </div>
        <div className="rounded-xl p-4 mb-6 border" style={{ backgroundColor: `${CORAL}0D`, borderColor: `${CORAL}40` }}>
          <h3 className="font-semibold mb-2" style={{ color: CORAL }}>{t('settings.deleteModal.willDelete')}</h3>
          <ul className="text-sm space-y-1 list-disc list-inside" style={{ color: TEXT_DESC }}>
            <li>{t('settings.deleteModal.item1')}</li>
            <li>{t('settings.deleteModal.item2')}</li>
            <li>{t('settings.deleteModal.item3')}</li>
            <li>{t('settings.deleteModal.item4')}</li>
          </ul>
        </div>
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: TEXT_HEADING }}>{t('settings.deleteModal.typeDelete')} <span className="font-bold" style={{ color: CORAL }}>DELETE</span></label>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={t('settings.deleteModal.typeDeletePlaceholder')}
              className="block w-full px-4 py-3 min-h-[48px] text-base border border-[#E1E4E8] rounded-xl focus:ring-2 focus:ring-[#006D77]/30"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: TEXT_HEADING }}>{t('settings.deleteModal.enterPassword')}</label>
            <input
              type="password"
              value={deleteConfirmPassword}
              onChange={(e) => setDeleteConfirmPassword(e.target.value)}
              placeholder={t('settings.deleteModal.passwordPlaceholder')}
              className="block w-full px-4 py-3 min-h-[48px] text-base border border-[#E1E4E8] rounded-xl focus:ring-2 focus:ring-[#006D77]/30"
            />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); setDeleteConfirmPassword(''); }}
            disabled={isDeleting}
            className="flex-1 py-3 min-h-[48px] font-space font-semibold rounded-xl border border-[#E1E4E8] active:scale-[0.98] disabled:opacity-50"
            style={{ color: TEXT_HEADING }}
          >
            {t('settings.deleteModal.cancel')}
          </button>
          <button
            onClick={handleDeleteAccount}
            disabled={isDeleting || deleteConfirmText !== 'DELETE' || !deleteConfirmPassword}
            className="flex-1 py-3 min-h-[48px] font-space font-semibold rounded-xl active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: CORAL, color: '#fff' }}
          >
            {isDeleting ? t('settings.deleteModal.deleting') : t('settings.deleteModal.deleteForever')}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="animate-fade-in bg-[#F8FAFA] min-h-full safe-area-inset relative overflow-hidden">
      <GeometricBackground />

      <div className="relative z-10 max-w-7xl mx-auto py-3 sm:py-6 lg:py-12 px-4 sm:px-6 lg:px-8 pb-24 sm:pb-24">
        {/* Header, on mobile only when menu list is shown; on desktop always. Hidden in detail view (back button shown). */}
        <div className={`${mobileView !== 'menu' ? 'hidden' : ''} lg:block mb-4 sm:mb-6 lg:mb-10`}>
          <h1 className="font-space text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-black tracking-tight leading-tight" style={{ color: TEXT_HEADING }}>
            {t('settings.header.title')}
          </h1>
          <p className="mt-1 sm:mt-2 text-sm sm:text-base md:text-lg" style={{ color: TEXT_DESC }}>
            {t('settings.header.subtitle')}
          </p>
        </div>

        {/* Mobile: Master (menu) or Detail (content). lg: always side-by-side. */}
        <div className="lg:grid lg:grid-cols-4 lg:gap-6 xl:gap-8">
          {/* Sidebar, hidden on mobile when a section is open; always visible on lg */}
          <div className={`${mobileView !== 'menu' ? 'hidden' : ''} lg:block lg:col-span-1`}>
            <nav className="space-y-2 lg:bg-white lg:rounded-2xl lg:shadow-sm lg:border lg:border-[#E1E4E8] lg:p-2">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => selectTab(tab.id)}
                  className={`w-full flex items-center px-4 py-3 text-left font-space font-semibold rounded-xl transition-all touch-manipulation min-h-[48px] text-base active:scale-[0.98] ${activeTab === tab.id
                    ? 'text-white shadow-md'
                    : 'bg-white lg:bg-transparent border border-[#E1E4E8] lg:border-0 text-[#1E1E1E] active:bg-gray-100'
                  }`}
                  style={activeTab === tab.id ? { backgroundColor: DEEP_TEAL } : {}}
                >
                  {tab.icon}
                  <span className="ml-3">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Main content, hidden on mobile when menu is shown; back on mobile via bottom nav only */}
          <div className={`${mobileView === 'menu' ? 'hidden' : ''} lg:block lg:col-span-3`}>
            <div className="lg:bg-white lg:rounded-2xl lg:shadow-sm lg:border lg:border-[#E1E4E8] p-4 sm:p-6 lg:p-8">
              {renderTabContent()}

              {/* Save section */}
              <div className="mt-8 md:mt-10 pt-6 md:pt-8 border-t border-[#E1E4E8] flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4">
                {saveStatus && (
                  <span className="font-space text-sm font-medium flex items-center flex-1" style={{ color: SAGE }}>
                    <CheckCircle className="inline w-5 h-5 mr-1.5 flex-shrink-0" />
                    {saveStatus}
                  </span>
                )}
                <button
                  onClick={handleSave}
                  disabled={isLoading}
                  className={`w-full sm:w-auto px-8 py-4 min-h-[48px] rounded-2xl font-space font-black text-lg transition-all touch-manipulation active:scale-[0.98] flex items-center justify-center gap-2 shrink-0 ${isLoading ? 'bg-gray-200 opacity-60 cursor-not-allowed' : ''}`}
                  style={!isLoading ? { backgroundColor: DEEP_TEAL, color: '#fff' } : {}}
                >
                  <Check className="w-5 h-5 flex-shrink-0" />
                  {isLoading ? t('settings.buttons.saving') : t('settings.buttons.saveChanges')}
                </button>
              </div>

              {/* Account Actions / Danger Zone. Logout */}
              {onLogout && (
                <div className="mt-8 md:mt-10 pt-6 md:pt-8 border-t border-[#E1E4E8]">
                  <h3 className="font-space text-lg font-bold mb-3" style={{ color: TEXT_HEADING }}>
                    {t('settings.account.accountActions', { defaultValue: 'Account Actions' })}
                  </h3>
                  <button
                    type="button"
                    onClick={onLogout}
                    className="flex items-center gap-2 px-4 py-2 text-red-600 bg-red-50 hover:bg-red-100 font-medium rounded-xl transition-colors touch-manipulation min-h-[48px]"
                  >
                    <LogOut className="w-5 h-5 flex-shrink-0" />
                    {t('sidebar.logout', { defaultValue: 'Log out' })}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <>
          <DeleteAccountBottomSheet />
          <DeleteAccountModalDesktop />
        </>
      )}

      <GrammarBottomNav
        buttons={[
          { id: 'back', icon: ArrowLeft, label: t('nav.back', { defaultValue: 'Back' }), onClick: () => (window.history.length > 1 ? navigate(-1) : navigate('/profile')), color: DEEP_TEAL },
          { id: 'home', icon: Home, label: t('nav.home', { defaultValue: 'Home' }), onClick: () => navigate('/dashboard'), color: DEEP_TEAL },
          { id: 'profile', icon: User, label: t('nav.profile', { defaultValue: 'Profile' }), onClick: () => navigate('/profile'), color: DEEP_TEAL, active: true },
        ]}
      />
    </div>
  );
};

export default SettingsPage;
